import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeDollarSign,
  Building2,
  PlugZap,
  Target,
  TrendingDown,
  Users,
} from 'lucide-react';
import { formatMoney, formatNumber, formatPct, lastNDays, type Period } from '@/lib/format';
import { StatTile } from '@/components/charts/StatTile';
import { BarChartCard } from '@/components/charts/BarChartCard';
import { PageHeader } from '@/components/common/PageHeader';
import { ErrorState } from '@/components/common/QueryState';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader } from '@/components/ui/Card';
import { DateRangePicker } from '@/components/ui/DateRangePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCards, SkeletonTable } from '@/components/ui/Skeleton';
import { Table, type Column } from '@/components/ui/Table';
import { useAgencyDashboard } from './api';

interface AlertRow {
  id: string;
  label: string;
  detail: string;
  to?: string;
}

function AlertList({
  title,
  description,
  rows,
  tone,
  icon,
  emptyMessage,
}: {
  title: string;
  description?: string;
  rows: AlertRow[];
  tone: 'warning' | 'danger' | 'info';
  icon: ReactNode;
  emptyMessage: string;
}) {
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {icon}
            {title}
          </span>
        }
        description={description}
        actions={<Badge tone={rows.length > 0 ? tone : 'success'}>{rows.length}</Badge>}
      />
      {rows.length === 0 ? (
        <EmptyState compact title="Nada a tratar" description={emptyMessage} />
      ) : (
        <ul className="divide-y divide-line">
          {rows.slice(0, 6).map((row) => (
            <li key={row.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm sm:px-5">
              <span className="min-w-0">
                <span className="block truncate font-medium text-fg">{row.label}</span>
                <span className="block truncate text-xs text-muted">{row.detail}</span>
              </span>
              {row.to && (
                <Link to={row.to} className="shrink-0 text-xs font-medium text-primary hover:underline">
                  Abrir
                </Link>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function AgencyDashboardPage() {
  const [period, setPeriod] = useState<Period>(() => lastNDays(30));
  const { data, isLoading, isError, error, refetch } = useAgencyDashboard(period);

  const topClientColumns: Column<NonNullable<typeof data>['topClients'][number]>[] = [
    {
      id: 'client',
      header: 'Cliente',
      cell: (row) => (
        <Link to={`/clientes/${row.clientId}/painel`} className="font-medium text-primary hover:underline">
          {row.clientName}
        </Link>
      ),
    },
    { id: 'spend', header: 'Investimento', align: 'right', cell: (row) => formatMoney(row.spendCents) },
    { id: 'leads', header: 'Leads', align: 'right', cell: (row) => formatNumber(row.leads) },
    { id: 'cpl', header: 'CPL', align: 'right', cell: (row) => formatMoney(row.cplCents) },
  ];

  const topCampaignColumns: Column<NonNullable<typeof data>['topCampaigns'][number]>[] = [
    { id: 'name', header: 'Campanha', cell: (row) => <span className="font-medium text-fg">{row.name}</span> },
    { id: 'client', header: 'Cliente', hideOnMobile: true, cell: (row) => row.clientName },
    { id: 'leads', header: 'Leads', align: 'right', cell: (row) => formatNumber(row.leads) },
    { id: 'cpl', header: 'CPL', align: 'right', cell: (row) => formatMoney(row.cplCents) },
  ];

  const plannedVsSpent =
    data?.plannedVsSpent.map((row) => ({
      label: row.clientName,
      previsto: row.plannedCents / 100,
      consumido: row.spentCents / 100,
    })) ?? [];

  return (
    <>
      <PageHeader
        title="Painel da agência"
        description="Visão consolidada de investimento, resultado e pendências operacionais de todos os clientes."
        actions={<DateRangePicker value={period} onChange={setPeriod} className="w-full sm:w-72" />}
      />

      {isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <div className="space-y-5">
          <SkeletonCards />
          <div className="surface-card">
            <SkeletonTable />
          </div>
        </div>
      ) : !data ? (
        <div className="surface-card">
          <EmptyState
            title="Sem dados no período"
            description="Assim que houver sincronização de métricas, os indicadores aparecerão aqui."
          />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label="Investimento total"
              value={formatMoney(data.totalSpendCents)}
              current={data.totalSpendCents}
              previous={data.previousSpendCents}
              icon={<BadgeDollarSign className="h-4 w-4" />}
            />
            <StatTile
              label="Clientes ativos"
              value={formatNumber(data.activeClients)}
              icon={<Building2 className="h-4 w-4" />}
            />
            <StatTile
              label="Leads gerados"
              value={formatNumber(data.totalLeads)}
              icon={<Users className="h-4 w-4" />}
            />
            <StatTile
              label="CPL médio"
              value={formatMoney(data.averageCplCents)}
              lowerIsBetter
              icon={<Target className="h-4 w-4" />}
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <AlertList
              title="Contas sem conversão"
              description="Investimento sem lead registrado no período."
              tone="danger"
              icon={<AlertTriangle className="h-4 w-4 text-danger" />}
              emptyMessage="Todos os clientes com investimento registraram conversões."
              rows={data.clientsWithoutConversions.map((row) => ({
                id: row.clientId,
                label: row.clientName,
                detail: `${formatMoney(row.spendCents)} investidos sem conversão`,
                to: `/clientes/${row.clientId}/painel`,
              }))}
            />

            <AlertList
              title="Campanhas limitadas por orçamento"
              description="Poderiam entregar mais com verba adicional."
              tone="warning"
              icon={<TrendingDown className="h-4 w-4 text-warning" />}
              emptyMessage="Nenhuma campanha limitada por orçamento."
              rows={data.budgetLimitedCampaigns.map((row) => ({
                id: row.campaignId,
                label: row.name,
                detail: row.clientName,
                to: '/campanhas',
              }))}
            />

            <AlertList
              title="Anúncios reprovados"
              description="Precisam de ajuste de política antes de voltar a rodar."
              tone="danger"
              icon={<AlertTriangle className="h-4 w-4 text-danger" />}
              emptyMessage="Nenhum anúncio reprovado."
              rows={data.disapprovedAds.map((row) => ({
                id: row.adId,
                label: row.name,
                detail: `${row.clientName} — ${row.reason}`,
              }))}
            />

            <AlertList
              title="Quedas de desempenho"
              description="Comparação com o período anterior."
              tone="warning"
              icon={<TrendingDown className="h-4 w-4 text-warning" />}
              emptyMessage="Nenhuma queda relevante detectada."
              rows={data.performanceDrops.map((row) => ({
                id: row.clientId,
                label: row.clientName,
                detail: `Queda de ${formatPct(row.dropPct, 1)}`,
                to: `/clientes/${row.clientId}/painel`,
              }))}
            />

            <AlertList
              title="Contas desconectadas"
              description="Integrações que precisam ser reconectadas."
              tone="danger"
              icon={<PlugZap className="h-4 w-4 text-danger" />}
              emptyMessage="Todas as integrações estão conectadas."
              rows={data.disconnectedAccounts.map((row) => ({
                id: `${row.clientId}-${row.provider}`,
                label: row.clientName,
                detail: row.provider,
                to: '/integracoes',
              }))}
            />

            <BarChartCard
              title="Previsto × consumido"
              description="Orçamento planejado contra o efetivamente investido, em reais."
              data={plannedVsSpent}
              xKey="label"
              layout="vertical"
              height={Math.max(220, plannedVsSpent.length * 42)}
              yTickFormatter={(value) => formatMoney(value * 100)}
              series={[
                { key: 'previsto', label: 'Previsto', formatter: (value) => formatMoney(value * 100) },
                { key: 'consumido', label: 'Consumido', formatter: (value) => formatMoney(value * 100) },
              ]}
              emptyMessage="Nenhum orçamento planejado cadastrado para o período."
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h2 className="mb-2 text-sm font-semibold text-fg">Ranking de clientes</h2>
              <Table
                columns={topClientColumns}
                rows={data.topClients}
                rowKey={(row) => row.clientId}
                emptyTitle="Sem clientes com investimento"
                emptyDescription="Nenhum cliente registrou investimento no período selecionado."
              />
            </div>
            <div>
              <h2 className="mb-2 text-sm font-semibold text-fg">Ranking de campanhas</h2>
              <Table
                columns={topCampaignColumns}
                rows={data.topCampaigns}
                rowKey={(row) => row.campaignId}
                emptyTitle="Sem campanhas com resultado"
                emptyDescription="Nenhuma campanha registrou leads no período selecionado."
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
